import { IconButton, InputAdornment, Tooltip } from '@material-ui/core';
import React from 'react';

import { IPublisher } from '~/src/dash/api/console-api';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { IconText } from '~/src/dash/components/IconText';
import {
  ICustomRenderCellJSXProps,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { ISimpleOption, ISimpleOptionSelect, Text } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { IOfferNewConfig } from '~/src/dash/views/platform-connect/DraftOfferNew/config';
import { ISingleSelectOption } from '~/src/dash/views/singleselect-view';

export type orderDirection = 'asc' | 'desc';

export enum EPublisherType {
  PUBLISHER_TYPE_NONE = 0,
  PUBLISHER_TYPE_BUYER = 1,
  PUBLISHER_TYPE_SELLER = 2,
  PUBLISHER_TYPE_BOTH = 3,
}

export const validPublisherType = [
  EPublisherType.PUBLISHER_TYPE_BUYER,
  EPublisherType.PUBLISHER_TYPE_SELLER,
  EPublisherType.PUBLISHER_TYPE_BOTH,
];

export enum EPlatformConnectStatus {
  PLATFORM_CONNECTION_UNKNOWN = 0,
  PLATFORM_CONNECTION_INACTIVE = 1,
  PLATFORM_CONNECTION_LIVE = 2,
  PLATFORM_CONNECTION_READY_TO_ACCEPT = 3,
  PLATFORM_CONNECTION_NEW_COUNTER_OFFER = 4,
  PLATFORM_CONNECTION_OFFER_SENT = 5,
  PLATFORM_CONNECTION_OFFER_DECLINED = 6,
  PLATFORM_CONNECTION_OFFER_CANCELED = 7,
  PLATFORM_CONNECTION_OFFER_EXPIRED = 8,
}

export enum EPlatformConnectOfferType {
  PLATFORM_CONNECTION_DEAL_EXTENSION = 0,
  PLATFORM_CONNECTION_UNIQUE_DEMAND = 1,
  PLATFORM_CONNECTION_LEGACY = 2,
}

export enum EPlatformConnectionActionType {
  PLATFORM_CONNECTION_ACTION_TYPE_UNKNOWN = 0,
  PLATFORM_CONNECTION_ACTION_TYPE_NOTE = 1,
  PLATFORM_CONNECTION_ACTION_TYPE_NEW_OFFER = 2,
  PLATFORM_CONNECTION_ACTION_TYPE_COUNTER_OFFER = 3,
  PLATFORM_CONNECTION_ACTION_TYPE_ACCEPT = 4,
  PLATFORM_CONNECTION_ACTION_TYPE_DECLINE = 5,
  PLATFORM_CONNECTION_ACTION_TYPE_CANCEL = 6,
  PLATFORM_CONNECTION_ACTION_TYPE_INACTIVE = 7,
  PLATFORM_CONNECTION_ACTION_TYPE_ACTIVE = 8,
}

export const OfferCanBeAccepted = [
  EPlatformConnectStatus.PLATFORM_CONNECTION_NEW_COUNTER_OFFER,
  EPlatformConnectStatus.PLATFORM_CONNECTION_READY_TO_ACCEPT,
];

export const OfferDeclinedCanceledExpired = [
  EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_DECLINED,
  EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_CANCELED,
  EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_EXPIRED,
];

export const OfferTypeUnknownString: string = 'Unknown';
export const OfferTypeDealExtensionString: string = 'Deal Extension';
export const OfferTypeUniqueDemandString: string = 'Unique Demand';

export function platformConnectOfferTypeToString(platformConnectOfferType: number): string {
  switch (platformConnectOfferType) {
    case EPlatformConnectOfferType.PLATFORM_CONNECTION_DEAL_EXTENSION:
      return OfferTypeDealExtensionString;
    case EPlatformConnectOfferType.PLATFORM_CONNECTION_UNIQUE_DEMAND:
      return OfferTypeUniqueDemandString;
    default:
      return OfferTypeUnknownString;
  }
}

export function platformConnectOfferTypeToNumber(platformConnectOfferTypeString: string): number {
  switch (platformConnectOfferTypeString) {
    case OfferTypeDealExtensionString:
      return EPlatformConnectOfferType.PLATFORM_CONNECTION_DEAL_EXTENSION;
    case OfferTypeUniqueDemandString:
      return EPlatformConnectOfferType.PLATFORM_CONNECTION_UNIQUE_DEMAND;
    default:
      return 3; // unknown, maybe adjust values so unknown is 0
  }
}

export interface IPlatformConnectionOfferHistory {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  PublisherID: number;
  ProposedBidFloor: number;
  PlatformConnectionID: number;
  Note: string;
  ActionType: EPlatformConnectionActionType;
  UserID: number;
  User: any;
}

export interface IDemandSourcesIDAndName {
  ID: string; // spotx, beachfront
  Name: string; // Spotx, Beachfront
}

export interface IPlatformConnectUiParams {
  currPage?: number;
  rowsPerPage?: RowsPerPage;
  activeColumnKeys?: string[];
}

export interface IGetPlatformConnectAPIParams {
  page?: number;
  pageSize?: RowsPerPage;
}

export interface IGetUniqueOpportunitiesAPIParams {
  page?: number;
  pageSize?: RowsPerPage;
}

// duplicate or the one above, remove one
export interface IPlatformConnect {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  BuyerPublisherID: number;
  SellerPublisherID: number;
  ActionPublisherID: number;
  DealID: number;
  AudienceSegmentID: number;
  Status: EPlatformConnectStatus;
  BidFloor: number;
  DemandSource: string;
  ImpressionsGoal: number;
  BuyerChannelID: number;
  SellerBidderID: number;
  AdsTxt: string;
}

export interface IPlatformConnectionWithNamesAndType {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  BuyerPublisherID: number;
  SellerPublisherID: number;
  ActionPublisherID: number;
  PartnerPublisherName: string;
  DealID: number;
  AudienceSegmentID: number;
  Status: EPlatformConnectStatus;
  BidFloor: number;
  DemandSource: string;
  ImpressionsGoal: number;
  BuyerChannelID: number;
  SellerBidderID: number;
  PlatformConnectionOfferHistory: IPlatformConnectionOfferHistory[];
  Type: string;
  AdsTxt: string;
}

export interface IAudienceSegmentIDAndName {
  ID: string;
  Name: string;
}

export interface IDealIDAndName {
  ID?: string;
  Name?: string;
}

// combine pending offers and connections later on frontend and backend, depends on backend response for connections
export interface IPlatformConnectDealExtForecast {
  PublisherID: number;
  OpportunityPotential: string;
  AvgImpEcpm: number;
  PlatformConnectSegmentQuery: string;
  UniqueUsersHLLSketchForSegmentQuery: string;
  uniqueUsersHLLSketchesForPublishersQuery: string;
  HasPendingOffer: boolean;
}

export interface IPlatformConnectStatus {
  HasPendingOffer: boolean;
  HasConnection: boolean;
}

export interface IPlatformConnectionDealExtRow {
  ID: number;
  Name: string;
  Status: IPlatformConnectStatus;
}

export interface IPlatformConnectionUniqueDemandRow {
  ID: number;
  Name: string;
  DemandMatch: string; // comma separated demand sources
  HasPendingOffer: boolean;
}

export interface IGetPendingOffersResponse {
  PendingOffers: IPlatformConnectionWithNamesAndType[];
  TotalRows: number;
}

export interface IGetConnectionsResponse {
  Connections: IPlatformConnectionWithNamesAndType[];
  TotalRows: number;
}
export interface IGetUniqueDemandOpportunitiesResponse {
  UniqueDemandOpportunities: IPlatformConnectUniqueDemandOpportunities[];
  TotalRows: number;
}

export interface IGetOfferHistoryResponse {
  OfferHistories: IPlatformConnectionOfferHistory[];
}

export interface ISellerPublisherAndStatus {
  SellerPublisher: IPublisher;
  Status: IPlatformConnectStatus;
}

export interface IGetDealExtSellerPublishersResponse {
  DealExtSellerPublishers: ISellerPublisherAndStatus[];
  TotalRows: number;
}

export interface IPlatformConnectProps {
  cfg: IOfferNewConfig;
  currOfferType: EPlatformConnectOfferType;
  filters: IPlatformConnectListFilters;
  selectedValues: IPlatformConnectSelectedValue;
  handleOnChange: (key: string, value: string | number | string[] | IMenuList) => void;
  onAcceptDemandSource?: (key: string) => void;
  onSwitchOfferType: () => void;
  loadState: LoadingState;
  uniqueDemandOpportunitiesRows?: IPlatformConnectionUniqueDemandRow[];
}

export interface IPlatformConnectDealExtensionProps {
  cfg: IOfferNewConfig;
  filters: IPlatformConnectListFilters;
  selectedValues: IPlatformConnectSelectedValue;
  onChange: (key: string, value: string | number | string[] | IMenuList) => void;
  loadState: LoadingState;
  dealExtensionOpportunitiesRows: IPlatformConnectionDealExtRow[];
  fetchSellerPublishers: (params: IPlatformConnectUiParams) => void;
  totalRowCount: number;
  handleClickOpenDrawer: (props: ICustomRenderCellJSXProps) => void;
  disableDealExtOpportunities: boolean;
}

export interface IPlatformConnectUniqueDemandProps {
  cfg: IOfferNewConfig;
  filters: IPlatformConnectListFilters;
  onChange: (key: string, value: string | number | string[] | IMenuList) => void;
  onAcceptDemandSource: (key: string) => void;
  loadState: LoadingState;
  uniqueDemandOpportunitiesRows: IPlatformConnectionUniqueDemandRow[];
  fetchUniqueDemandOpportunities: (params: IPlatformConnectUiParams) => void;
  totalRowCount: number;
  handleClickOpenDrawer: (props: ICustomRenderCellJSXProps) => void;
  disableUniqueDemandOpportunities: boolean;
}

export interface IPlatformConnectListFilters {
  [key: string]: ISingleSelectOption[] | IMenuList;
}

// key = audienceSegments/impressionsGoal/uniqueDemands/deal/demandSource
export interface IPlatformConnectSelectedValue {
  [key: string]: string | number | string[] | IDemandSourcesIDAndName[] | IAudienceSegmentIDAndName;
}

export interface IPlatformConnectUniqueDemandOpportunities {
  DemandMatch: string;
  SellerPublisher: IPublisher;
}

export const convertPlatformConnectStatusValueToColor = (statusValue: number) => {
  switch (statusValue) {
    case EPlatformConnectStatus.PLATFORM_CONNECTION_READY_TO_ACCEPT:
      return 'green';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_NEW_COUNTER_OFFER:
      return 'purple';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_SENT:
      return 'dark green';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_DECLINED:
      return 'red';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_CANCELED:
      return 'maroon';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_EXPIRED:
      return 'peach';
    default:
      return '';
  }
};

export const convertPlatformConnectStatusValueToText = (statusValue: number) => {
  switch (statusValue) {
    case EPlatformConnectStatus.PLATFORM_CONNECTION_INACTIVE:
      return 'Inactive';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_LIVE:
      return 'Live';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_READY_TO_ACCEPT:
      return 'Ready to Accept';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_NEW_COUNTER_OFFER:
      return 'New Offer';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_SENT:
      return 'Offer Sent';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_DECLINED:
      return 'Offer Declined';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_CANCELED:
      return 'Offer Canceled';
    case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_EXPIRED:
      return 'Offer Expired';
    default:
      return '';
  }
};

export const offerTypes: Array<ISimpleOption> = [
  {
    key: 'offerType',
    value: String(EPlatformConnectOfferType.PLATFORM_CONNECTION_DEAL_EXTENSION),
    label: 'Deal Extension',
    icon: 'handshake',
  },
  {
    key: 'offerType',
    value: String(EPlatformConnectOfferType.PLATFORM_CONNECTION_UNIQUE_DEMAND),
    label: 'Unique Demand',
    icon: 'globe',
  },
];

// helper to format options for SinpleSingleSelect
export const getOptions = (
  items: Array<ISimpleOption>,
  selectedValue: string,
  isOptionValid: (value: ISimpleOption) => boolean = () => false,
): Array<ISimpleOptionSelect> => {
  return items.map((item) => ({
    ...item,
    isSelected: item.value === selectedValue,
    isDisabled: !isOptionValid(item),
  }));
};

// formats long strings to break with an ellipsis, adds a tooltip to display full value, opens drawer
export const EllipsisWithTooltipAndOpenDrawerRender = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;

  return (
    <Tooltip title={value} placement='top-start'>
      <div
        onClick={() => customFunctions.handleClickOpenDrawer(props)}
        style={{
          cursor: 'pointer',
        }}
      >
        <Text
          small
          style={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {value}
        </Text>
      </div>
    </Tooltip>
  );
};

export const ImpressionsOpenDrawerRender = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();

  return (
    <Tooltip title='Impression goals only populate for Deal Extension' placement='top-start'>
      <div
        onClick={() => customFunctions.handleClickOpenDrawer(props)}
        style={{
          cursor: 'pointer',
        }}
      >
        <Text small>{props.row?.Type?.value === 'Deal Extension' ? props.value : 'N/A'}</Text>
      </div>
    </Tooltip>
  );
};

export const CPMOpenDrawerRender = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();

  return (
    <div
      onClick={() => customFunctions.handleClickOpenDrawer(props)}
      style={{
        cursor: 'pointer',
      }}
    >
      <Text small>{customFunctions.handleGetCurrencyValue(props.row)}</Text>
    </div>
  );
};

export const PlatformConnectTypeDisplay = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  if (props.row.Type.value === 'Deal Extension') {
    return (
      <IconText
        enableBorder
        small
        bold
        color={publicaColors.darkGray}
        icon='handshake'
        iconColor={publicaColors.black}
        text='Deal Extension'
        onClick={() => customFunctions.handleClickOpenDrawer(props)}
        style={{
          cursor: 'pointer',
        }}
      />
    );
  } else if (props.row.Type.value === 'Unique Demand') {
    return (
      <IconText
        enableBorder
        small
        bold
        color={publicaColors.darkGray}
        icon='globe'
        text='Unique Demand'
        onClick={() => customFunctions.handleClickOpenDrawer(props)}
        style={{
          cursor: 'pointer',
        }}
      />
    );
  } else if (props.row.Type.value === 'Legacy') {
    return (
      <IconText
        enableBorder
        small
        bold
        color={publicaColors.darkGray}
        icon='swap'
        text='Legacy'
        onClick={() => customFunctions.handleClickOpenDrawer(props)}
        style={{
          cursor: 'pointer',
        }}
      />
    );
  } else {
    return <IconText small color={publicaColors.darkGray} icon='' text='' />;
  }
};

export const NotesSidebarLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();

  return (
    <div
      onClick={() => customFunctions.handleClickOpenDrawer(props)}
      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      <IconButton aria-label='Note' style={{ padding: '4px' }}>
        <IconV2 color={publicaColors.veryDarkGray} name='message' />
      </IconButton>
    </div>
  );
};

export const parseBidderParamsSafely = (str: string) => {
  try {
    return JSON.parse('{' + str + '}');
  } catch (e) {
    // Return an empty object
    return {};
  }
};

// format request params
export const formatParams = (params: IPlatformConnectUiParams): IGetPlatformConnectAPIParams => ({
  page: params.currPage,
  pageSize: params.rowsPerPage,
});

export const renderExchangeAdornment = (
  value: string,
  currencySymbol: string,
  exchangeValueToUSD: number,
) => {
  if (value === undefined || value === '' || currencySymbol === '$') {
    return <></>;
  }

  return (
    <InputAdornment position='end'>
      {currencySymbol + (parseFloat(value.replace(/,/g, '')) * exchangeValueToUSD).toFixed(2)}
    </InputAdornment>
  );
};

export const RowLineBreak = () => {
  return (
    <div
      style={{
        height: '1px',
        borderBottom: `1px solid ${publicaColors.lightGray}`,
        margin: '0px 24px',
      }}
    />
  );
};
